<template>
	<Accordion
		:isExpandable="company.locations.length > 0"
		:isOpen="isOpenStatus"
		@expanded="onAccordionExpanded"
		variant="accordion--vertical"
		ref="accordion"
	>
		<template #main>
			<Card class="company-card" :highlights="exactMatch" ref="card">
				<template #content>
					<div class="company-card__main">
						<div class="company-card__icon">
							<i class="icon icon-company" />
						</div>
						<span class="company-card__number">
							({{ company.companyNumber }})
						</span>
						<Ellipsis
							:data="company.companyName"
							:textToHighlight="$route.query.search"
							class="company-card__name"
						></Ellipsis>
					</div>
				</template>
				<template #actions>
					<ActionsSet
						class="company-card__actions"
						:options="actions"
						ref="actionsSet"
					/>
				</template>
			</Card>
		</template>
		<template #body-content>
			<ul class="company-card__location-card-list">
				<li
					class="company-card__location-card-list-item"
					v-for="(location, index) in company.locations"
					:key="location.id"
					:style="{
						zIndex: company.locations.length - index
					}"
				>
					<LocationCard
						:location="location"
						:customer="customer"
						:company="company"
						ref="locationCard"
					/>
				</li>
			</ul>
		</template>
	</Accordion>
</template>

<script>
import Accordion from '@/components/shells/accordion/accordion';
import LocationCard from './location-card';
import Card from '@/components/shells/card/card';
import ActionsSet from '@/site-components/actions-set/actions-set';
import Ellipsis from '@/components/visual/ellipsis';

// mixins
import CustomerCardActions from '@/mixins/customers/customer-card-action';
import AccordionStatus from './accordions-status-mixin';

// utils
import { createTextValueObj } from '@/utils/object';

export default {
	name: 'CompanyCard',
	components: {
		Accordion,
		LocationCard,
		Card,
		ActionsSet,
		Ellipsis
	},
	mixins: [CustomerCardActions, AccordionStatus],
	props: {
		company: {
			type: Object,
			default: () => {}
		},
		customer: {
			type: Object,
			default: () => {}
		}
	},
	created() {
		if (!this.company.locations.length) {
			return;
		}

		if (this.$route.query.search) {
			this.onAccordionExpanded(true);
		} else {
			const hasAccordionStoredStatus = this.openStatusStored(
				'companiesAccordionStatuses',
				this.company.id
			);

			if (hasAccordionStoredStatus) {
				this.isOpenStatus = true;
			}
		}
	},
	computed: {
		displayCompanyName() {
			return createTextValueObj(
				this.company.companyName,
				this.$route.query.search
			);
		},
		exactMatch() {
			return (
				this.$route.query.search &&
				this.company.companyName === this.$route.query.search
			);
		}
	},
	methods: {
		onAccordionExpanded(status) {
			this.updateAccordionStatuses({
				id: this.company.id,
				status,
				name: 'companiesAccordionStatuses'
			});

			this.isOpenStatus = status;
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Company Card
    * Can use SCSS variables = TRUE
 */

@import '@/scss/5-objects/_cards.scss';

.company-card {
	@include card('accordion');
	&__main {
		display: flex;
		align-items: center;
		height: 100%;
		gap: $gutter-md;
	}

	&__icon {
		position: relative;
		line-height: 1;

		.icon-company {
			font-size: 18px;
		}

		.badge-count--absolute {
			right: -8px;
			top: -8px;
		}
	}

	&__number {
		@include body-text('regular');
	}

	&__name {
		@include heading('md');
		text-transform: capitalize;
		line-height: 1;
	}

	&__location-card-list {
		display: flex;
		flex-direction: column;
	}

	&__location-card-list-item {
		margin-top: $gutter-md;
	}
}

.no-expandable .company-card {
	padding-left: 32px;
}

::v-deep .accordion__main {
	border-radius: 0 4px 4px 0;
}
</style>
